import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronDown, CheckCircle, Sparkles, Clock, Lock as LockIcon } from "lucide-react";
import { ASSESSMENTS } from "@/data/assessments";
import { generateAxisSampleReport } from "@/utils/axisSampleReport";
import axios from "axios";
import usePageTitle from "@/hooks/usePageTitle";

const API = process.env.REACT_APP_BACKEND_URL;

function FactsAccordion({ facts }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-navy/10 pt-4">
      <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full text-left" data-testid="facts-toggle">
        <span className="font-dm text-navy font-semibold text-sm">Interesting Facts</span>
        <ChevronDown className={`w-4 h-4 text-gold transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul className="mt-3 space-y-2">
          {facts.map((f, i) => (
            <li key={i} className="font-dm text-navy/70 text-xs leading-relaxed flex gap-2">
              <span className="text-gold mt-0.5">▸</span>{f}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function WaitlistForm({ assessmentKey }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!email.includes("@")) { setError("Please enter a valid email"); return; }
    try {
      await axios.post(`${API}/api/assessments/waitlist`, { assessment: assessmentKey, email });
      setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to join waitlist");
    }
  };

  if (done) return (
    <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
      <span className="font-dm text-green-700 text-xs">You're on the waitlist!</span>
    </div>
  );

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" data-testid={`waitlist-email-${assessmentKey}`}
          className="flex-1 border border-navy/20 rounded-lg px-3 py-2 font-dm text-xs focus:border-gold focus:outline-none" />
        <button onClick={submit} data-testid={`waitlist-submit-${assessmentKey}`}
          className="bg-gold text-navy font-dm font-bold px-4 py-2 rounded-lg text-xs hover:bg-gold/90 transition-colors whitespace-nowrap">
          Notify Me
        </button>
      </div>
      {error && <p className="font-dm text-red-600 text-xs">{error}</p>}
    </div>
  );
}

function AssessmentCard({ a, status }) {
  const isLive = status === "LIVE";
  const isComingSoon = status === "COMING_SOON";

  return (
    <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
      className="relative bg-white border-t-4 border-gold rounded-xl shadow-lg overflow-hidden hover:-translate-y-2 transition-transform duration-300 flex flex-col"
      data-testid={`assessment-card-${a.key}`}>
      {/* Status badge */}
      <div className="absolute top-4 right-4 z-10">
        {isLive ? (
          <span className="inline-flex items-center gap-1.5 bg-green-500 text-white font-dm text-xs font-bold px-3 py-1 rounded-full">
            <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" /> LIVE
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 bg-gold text-navy font-dm text-xs font-bold px-3 py-1 rounded-full">
            <Clock className="w-3 h-3" /> Coming Soon
          </span>
        )}
      </div>

      {/* Header illustration / gradient */}
      <div className="h-36 bg-gradient-to-br from-navy to-navy/80 relative overflow-hidden flex items-center justify-center">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 50%, #C9A84C 0%, transparent 50%), radial-gradient(circle at 80% 80%, #C9A84C 0%, transparent 50%)" }} />
        <div className="relative grid grid-cols-2 gap-2">
          {a.dimensions.slice(0, 4).map((d, i) => (
            <div key={i} className={`w-12 h-12 rounded-lg flex items-center justify-center font-playfair text-xl font-bold ${i % 2 === 0 ? "bg-gold text-navy" : "bg-white/10 text-gold border border-gold/40"}`}>
              {d.letter}
            </div>
          ))}
        </div>
      </div>

      <div className="p-7 flex-1 flex flex-col">
        <h3 className="font-playfair text-navy text-2xl font-bold mb-1">{a.title}</h3>
        <p className="font-dm text-gold text-sm font-semibold mb-3">{a.subtitle}</p>
        <p className="font-dm text-navy/70 text-sm leading-relaxed mb-5">{a.description}</p>

        {/* Dimension chips */}
        <div className="grid grid-cols-2 gap-2 mb-5">
          {a.dimensions.map((d, i) => (
            <div key={i} className="bg-cream rounded-lg px-3 py-2 border border-navy/5">
              <div className="flex items-baseline gap-1.5">
                <span className="font-playfair text-gold font-bold text-base">{d.letter}</span>
                <span className="font-dm text-navy text-xs font-semibold">{d.name}</span>
              </div>
              <p className="font-dm text-navy/50 text-[10px] leading-snug mt-0.5">{d.line}</p>
            </div>
          ))}
        </div>

        {/* Helps section (only for live) */}
        {a.helps && (
          <div className="grid grid-cols-3 gap-2 mb-5">
            <div className="bg-navy/5 rounded p-2">
              <p className="font-dm text-navy text-[10px] font-bold uppercase tracking-wide mb-0.5">Individual</p>
              <p className="font-dm text-navy/60 text-[10px] leading-snug">{a.helps.individual}</p>
            </div>
            <div className="bg-navy/5 rounded p-2">
              <p className="font-dm text-navy text-[10px] font-bold uppercase tracking-wide mb-0.5">Leader</p>
              <p className="font-dm text-navy/60 text-[10px] leading-snug">{a.helps.leader}</p>
            </div>
            <div className="bg-navy/5 rounded p-2">
              <p className="font-dm text-navy text-[10px] font-bold uppercase tracking-wide mb-0.5">Organisation</p>
              <p className="font-dm text-navy/60 text-[10px] leading-snug">{a.helps.organisation}</p>
            </div>
          </div>
        )}

        <FactsAccordion facts={a.facts} />

        {/* Pricing & CTA */}
        <div className="mt-5 pt-5 border-t border-navy/10">
          {isLive ? (
            <>
              <div className="bg-gold/10 border border-gold/30 rounded-lg p-3 mb-3 flex items-center justify-between">
                <div>
                  <p className="font-dm text-navy/60 text-[10px] uppercase tracking-wide">Cost</p>
                  <p className="font-playfair text-navy font-bold text-lg">{a.cost} <span className="text-xs font-dm font-semibold text-navy/60">assessment tokens</span></p>
                </div>
                <Sparkles className="w-6 h-6 text-gold" />
              </div>
              <Link to={a.href} data-testid={`assessment-cta-${a.key}`}
                className="block w-full bg-navy text-gold font-dm font-bold py-3 rounded-lg text-center hover:bg-navy/90 transition-colors">
                {a.cta}
              </Link>
              {a.key === "axis" && (
                <button onClick={generateAxisSampleReport} data-testid="axis-sample-report-btn"
                  className="mt-2 block w-full border border-gold text-gold font-dm font-bold py-2.5 rounded-lg text-center hover:bg-gold/10 transition-colors text-sm">
                  Download Sample Report (PDF)
                </button>
              )}
            </>
          ) : (
            <>
              <div className="bg-navy/5 rounded-lg p-3 mb-3">
                <p className="font-dm text-navy/60 text-xs">Estimated pricing on launch</p>
                <p className="font-playfair text-navy/40 font-bold text-lg line-through">From {a.estimatedFrom} per person</p>
              </div>
              <p className="font-dm text-navy/70 text-xs mb-2 font-semibold">Notify me when this launches</p>
              <WaitlistForm assessmentKey={a.key} />
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function AssessmentsPage() {
  usePageTitle("Assessments");
  const [toggles, setToggles] = useState({});

  useEffect(() => {
    axios.get(`${API}/api/feature-toggles`).then(r => setToggles(r.data)).catch(() => {});
  }, []);

  const visibleAssessments = ASSESSMENTS.filter(a => (toggles[a.key] || "LIVE") !== "DISABLED");

  return (
    <div className="bg-cream min-h-screen">
      {/* Hero */}
      <section className="relative bg-navy pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: "linear-gradient(135deg, transparent 25%, #C9A84C 25%, #C9A84C 26%, transparent 26%, transparent 75%, #C9A84C 75%, #C9A84C 76%, transparent 76%)",
          backgroundSize: "60px 60px"
        }} />
        <div className="relative max-w-5xl mx-auto px-4 text-center">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="inline-block bg-gold/20 text-gold font-dm text-sm px-4 py-1.5 rounded-full mb-6 font-semibold">
            RoleIQ Assessments
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-playfair text-white text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Understand People Deeply.<br /><span className="text-gold">Build Stronger Teams.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="font-dm text-white/70 text-lg max-w-3xl mx-auto mb-8">
            Science-backed assessments that reveal how people think, lead, and grow — for individuals, leaders, and organisations across every industry.
          </motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#cards" className="bg-gold text-navy font-dm font-bold px-8 py-3 rounded-lg hover:bg-gold/90 transition-colors" data-testid="explore-assessments-btn">
              Explore Assessments
            </a>
            <Link to="/contact" className="border border-white text-white font-dm font-bold px-8 py-3 rounded-lg hover:bg-white/10 transition-colors">
              Book a Consultation
            </Link>
          </motion.div>
          <p className="font-dm text-white/50 text-sm mt-8">Used by HR leaders across India · Delivered by certified practitioners · Results in 48 hours</p>
        </div>
      </section>

      {/* Token note */}
      <section id="cards" className="max-w-5xl mx-auto px-4 -mt-8 mb-8 relative">
        <div className="bg-white border border-gold/30 rounded-xl shadow-lg p-5 flex items-start gap-3">
          <LockIcon className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-dm text-navy font-semibold text-sm">Assessment tokens are separate from your competency map tokens.</p>
            <p className="font-dm text-navy/60 text-xs mt-1">1 assessment token = ₹17.98 at Starter rate. <Link to="/pricing#assessment-packs" className="text-gold hover:underline font-semibold">View Assessment Token Packs →</Link></p>
          </div>
        </div>
      </section>

      {/* Cards grid */}
      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8">
            {visibleAssessments.map((a) => (
              <AssessmentCard key={a.key} a={a} status={toggles[a.key] || "LIVE"} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="bg-gold py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-playfair text-navy text-3xl font-bold mb-3">Ready to add assessments to your toolkit?</h2>
          <p className="font-dm text-navy/70 text-base mb-6">Buy assessment tokens and start understanding your team in a whole new way.</p>
          <Link to="/pricing#assessment-packs" className="inline-block bg-navy text-gold font-dm font-bold px-10 py-3 rounded-lg hover:bg-navy/90 transition-colors" data-testid="buy-assessment-tokens-btn">
            Buy Assessment Tokens
          </Link>
        </div>
      </section>
    </div>
  );
}
